import React, { useEffect, useState, useContext } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { XIcon, EyeOffIcon } from '@heroicons/react/outline';
import CharacterContext from '../context/CharacterContext';
import CampaignContext from '../context/CampaignContext';
import UserContext from '../context/UserContext';
import Characteristics from './Characteristics';
import Skills from './Skills';
import Inventory from './Inventory';
import MagicCardResume from './MagicCardResume';
import ConditionBadges from './ConditionBadges';
import { generateNpcName } from '../utils/npcGenerator';
import { getRoll } from '../utils/dice';
import '../styles/dmCharacterPanel.css';

const db = firebase.firestore();

const NOPIC = 'https://firebasestorage.googleapis.com/v0/b/beyondthedice-cfc1b.appspot.com/o/charactersPictures%2Fnopicture.png?alt=media&token=4a376f9c-0235-4b6c-889b-f1ffd6d12a48';

const DmCharacterPanel = ({ characterUid, onClose, sendNewRoll }) => {
  const [character, setCharacter] = useState(null);
  const [tab, setTab] = useState('stats');
  const [hideInventoryUpdate, setHideInventoryUpdate] = useState(false);
  const [draft, setDraft] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const { campaign } = useContext(CampaignContext);
  const { user } = useContext(UserContext);

  useEffect(() => {
    if (!characterUid) return;
    setTab('stats');
    const unsub = db.collection('characters').doc(characterUid).onSnapshot(snap => {
      if (snap.exists) setCharacter(snap.data());
    });
    return () => unsub();
  }, [characterUid]);

  const updateFirestoreCharacter = async (updated) => {
    updated.lastUpdateAt = firebase.firestore.FieldValue.serverTimestamp();
    await db.collection('characters').doc(updated.uid).set(updated);
  };

  const openEdit = () => {
    setDraft(JSON.parse(JSON.stringify(character)));
    setImageFile(null);
    setImagePreview(null);
    setTab('edit');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size >= 800000) { toast.error(i18next.t('imageToBig'), {}); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = (file, uid) => new Promise((resolve) => {
    const task = firebase.storage().ref(`charactersPictures/${uid}.png`).put(file);
    task.on('state_changed', null, (err) => { console.log('upload error', err); resolve(null); }, async () => {
      const url = await firebase.storage().ref('charactersPictures').child(`${uid}.png`).getDownloadURL();
      resolve(url);
    });
  });

  const saveEdit = async () => {
    setSaving(true);
    const updated = { ...draft };
    updated.currentHp = updated.currentHp === '' || updated.currentHp == null ? 0 : updated.currentHp;
    updated.maxHp = updated.maxHp === '' || updated.maxHp == null ? 0 : updated.maxHp;
    try {
      if (imageFile) {
        const url = await uploadImage(imageFile, updated.uid);
        if (url) updated.picture = url;
      }
      await updateFirestoreCharacter(updated);
      setCharacter(updated);
      setImageFile(null);
      setImagePreview(null);
      toast.success(i18next.t('update succed'), {});
      setTab('stats');
    } catch (err) {
      console.log('saveEdit error', err);
      toast.error(i18next.t('errorApi'), {});
    } finally {
      setSaving(false);
    }
  };

  // Le MJ lance les stats d'un PNJ : jet toujours caché (visible du MJ seul),
  // mais on garde le nom du PNJ plutôt que "MJ".
  const handleRollStat = (stat, type) => {
    if (!sendNewRoll || !character) return;
    const roll = getRoll(100, campaign.idUserDm, character, user, stat, true, type);
    roll.userName = character.name;
    sendNewRoll(roll);
  };

  const contextValue = {
    character: character || {},
    updateCharacter: (updated) => {
      setCharacter(updated);
      updateFirestoreCharacter(updated);
    },
  };

  if (!character) return (
    <div className="dmCharPanel">
      <div className="dmCharPanelHeader">
        <button className="dmCharPanelClose" onClick={onClose}><XIcon style={{ width: 18, height: 18 }} /></button>
      </div>
      <div className="dmCharPanelLoading">Chargement...</div>
    </div>
  );

  return (
    <CharacterContext.Provider value={contextValue}>
      <div className="dmCharPanel">
        <div className="dmCharPanelHeader">
          {character.picture
            ? <img src={character.picture} alt={character.name} className="dmCharPanelHero" style={{ objectPosition: `center ${({ top: '20%', center: '50%', bottom: '80%' })[character.picturePosition || 'center']}` }} />
            : <div className="dmCharPanelHeroPlaceholder" />
          }
          {character.picture && (
            <div className="dmCharPanelPosButtons">
              {['top', 'center', 'bottom'].map(pos => (
                <button
                  key={pos}
                  className={`dmCharPanelPosBtn${(character.picturePosition || 'center') === pos ? ' active' : ''}`}
                  onClick={() => {
                    const updated = { ...character, picturePosition: pos };
                    setCharacter(updated);
                    updateFirestoreCharacter(updated);
                  }}
                >
                  {pos === 'top' ? '▲' : pos === 'center' ? '●' : '▼'}
                </button>
              ))}
            </div>
          )}
          <div className="dmCharPanelHeaderOverlay">
            <div className="dmCharPanelIdentity">
              <strong>{character.name}{character.isNpc && <span className="dmCharPanelNpcTag">{i18next.t('npc.badge')}</span>}</strong>
              <span>{character.currentHp} / {character.maxHp} HP</span>
            </div>
            <button className="dmCharPanelClose" onClick={onClose}>
              <XIcon style={{ width: 18, height: 18 }} />
            </button>
          </div>
        </div>

        <ConditionBadges
          conditions={character.conditions || []}
          editable={true}
          onToggle={(key) => {
            const conditions = character.conditions || [];
            const updated = { ...character, conditions: conditions.includes(key) ? conditions.filter(k => k !== key) : [...conditions, key] };
            setCharacter(updated);
            updateFirestoreCharacter(updated);
          }}
        />

        <div className="dmCharPanelTabs">
          <button className={tab === 'stats' ? 'active' : ''} onClick={() => setTab('stats')}>Stats</button>
          <button className={tab === 'inventory' ? 'active' : ''} onClick={() => setTab('inventory')}>{i18next.t('inventory')}</button>
          {character.isMage && (
            <button className={tab === 'magic' ? 'active' : ''} onClick={() => setTab('magic')}>{i18next.t('mage.title')}</button>
          )}
          {character.isNpc && (
            <button className={tab === 'edit' ? 'active' : ''} onClick={openEdit}>{i18next.t('npc.edit')}</button>
          )}
        </div>

        <div className="dmCharPanelBody">
          {tab === 'stats' && (
            <>
              <p className="dmCharPanelSectionTitle">{i18next.t('characteristic')}</p>
              <div className="characteristicsDetail">
                <Characteristics
                  characteristics={character.characteristics || []}
                  campaign={{ clickStat: true }}
                  onRollStat={handleRollStat}
                />
              </div>
              <p className="dmCharPanelSectionTitle">{i18next.t('skill')}</p>
              <div className="skillsDetail">
                <Skills
                  skills={character.skills || []}
                  campaign={{ clickStat: true }}
                  hideRollSwitch={true}
                  onRollStat={handleRollStat}
                  sendNewRoll={() => {}}
                />
              </div>
            </>
          )}

          {tab === 'edit' && draft && (
            <div className="dmNpcEdit">
              <label className="dmEditLabel">{i18next.t('name')}</label>
              <div className="dmEditNameRow">
                <input
                  className="dmEditInput"
                  type="text"
                  value={draft.name || ''}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
                <button
                  type="button"
                  className="dmEditReroll"
                  title={i18next.t('npc.rerollName')}
                  onClick={() => setDraft({ ...draft, name: generateNpcName() })}
                >🎲</button>
              </div>

              <label className="dmEditLabel">{i18next.t('avatar')}</label>
              <div className="dmEditImageRow">
                <div
                  className="dmEditAvatarPreview"
                  style={{ backgroundImage: `url(${imagePreview || draft.picture || NOPIC})` }}
                />
                <label className="dmEditFileBtn">
                  {imageFile ? imageFile.name : i18next.t('choose a file')}
                  <input type="file" accept="image/*" hidden onChange={handleFile} />
                </label>
              </div>

              <div className="dmEditHpRow">
                <div className="dmEditHpCol">
                  <label className="dmEditLabel">{i18next.t('hp')}</label>
                  <input
                    className="dmEditInput"
                    type="number" min={0} max={90}
                    value={draft.currentHp}
                    onChange={(e) => setDraft({ ...draft, currentHp: e.target.value === '' ? '' : parseInt(e.target.value) })}
                  />
                </div>
                <div className="dmEditHpCol">
                  <label className="dmEditLabel">{i18next.t('hp max')}</label>
                  <input
                    className="dmEditInput"
                    type="number" min={0} max={90}
                    value={draft.maxHp}
                    onChange={(e) => setDraft({ ...draft, maxHp: e.target.value === '' ? '' : parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <p className="dmCharPanelSectionTitle">{i18next.t('characteristic')}</p>
              <div className="dmEditStatGrid">
                {(draft.characteristics || []).map((c, i) => (
                  <div key={c.label} className="dmEditStatRow">
                    <span>{i18next.t(`characteristics.${c.label}`)}</span>
                    <input
                      type="number" min={1} max={99}
                      value={c.value}
                      onChange={(e) => {
                        const characteristics = draft.characteristics.map((cc, idx) =>
                          idx === i ? { ...cc, value: e.target.value === '' ? '' : parseInt(e.target.value) } : cc);
                        setDraft({ ...draft, characteristics });
                      }}
                    />
                  </div>
                ))}
              </div>

              <p className="dmCharPanelSectionTitle">{i18next.t('skill')}</p>
              <div className="dmEditStatGrid">
                {(draft.skills || []).map((s, i) => (
                  <div key={i} className="dmEditStatRow">
                    <span>{s.isCustom ? s.label : i18next.t(`skills.${s.label}`)}</span>
                    <input
                      type="number" min={0} max={100}
                      value={s.value}
                      onChange={(e) => {
                        const skills = draft.skills.map((ss, idx) =>
                          idx === i ? { ...ss, value: e.target.value === '' ? '' : parseInt(e.target.value) } : ss);
                        setDraft({ ...draft, skills });
                      }}
                    />
                  </div>
                ))}
              </div>

              <button className="dmEditSaveBtn" disabled={saving} onClick={saveEdit}>
                {i18next.t('save')}
              </button>
            </div>
          )}

          {tab === 'inventory' && (
            <>
              <button
                className={`dmPanelHideInventoryBtn${hideInventoryUpdate ? ' active' : ''}`}
                onClick={() => setHideInventoryUpdate(v => !v)}
                title={i18next.t('hide inventory actions')}
              >
                <EyeOffIcon style={{ width: 14, height: 14 }} />
                <span>{i18next.t('hide inventory actions')}</span>
              </button>
              <Inventory
                updateInventory={(updated, rolls) => {
                  if (sendNewRoll && rolls && rolls.length > 0) {
                    const markedRolls = hideInventoryUpdate
                      ? rolls.map(r => ({ ...r, isHided: true }))
                      : rolls;
                    sendNewRoll(markedRolls);
                  }
                  setCharacter(updated);
                  updateFirestoreCharacter(updated);
                }}
                sendNewRoll={() => {}}
              />
            </>
          )}

          {tab === 'magic' && character.isMage && (
            <MagicCardResume
              cardsList={character.magicCards || []}
              isEditable={true}
              updateCards={(newList) => {
                const updated = { ...character, magicCards: newList };
                setCharacter(updated);
                updateFirestoreCharacter(updated);
              }}
            />
          )}
        </div>
      </div>
    </CharacterContext.Provider>
  );
};

export default DmCharacterPanel;
